import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { IconCash, IconReportAnalytics } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import {
  CashDrawerSession,
  CountDrawerResponse,
  DenominationEntry,
} from "../../types/cashDrawer";
import {
  computeVariance,
  denominationsToTotal,
  emptyDenominations,
  formatCurrency,
  todayInSalonTimezone,
} from "../../utils/cashDrawerUtils";
import { DenominationInput } from "./DenominationInput";

interface CountDrawerModalProps {
  opened: boolean;
  onClose: () => void;
  /**
   * Called after the user picks "Go to New Cash Drawer" on the
   * post-closeout choice screen. Parent should swap the visible session
   * to ``result.new_session`` and refresh history.
   */
  onCounted: (result: CountDrawerResponse) => void;
  /**
   * Called when the user picks "View Balancing Summary" on the
   * post-closeout choice screen. Parent should open the full-screen
   * summary modal for ``closedSession``.
   */
  onViewSummary: (closedSession: CashDrawerSession) => void;
  /** The session being counted (the current open drawer). */
  session: CashDrawerSession;
  /** Salon timezone — anchors the Balancing Date default to the salon's
   *  local "today" instead of the browser's local date. */
  timezone: string;
}

export const CountDrawerModal: React.FC<CountDrawerModalProps> = ({
  opened,
  onClose,
  onCounted,
  onViewSummary,
  session,
  timezone,
}) => {
  const { countCashDrawer } = useApp();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [denominations, setDenominations] =
    useState<DenominationEntry[]>(emptyDenominations());
  // null = checks not counted (we want to preserve the "didn't count"
  // distinction so the summary can render "—"); a number is the
  // captured total.
  const [countedChecks, setCountedChecks] = useState<number | null>(null);
  const [balancingDate, setBalancingDate] = useState<string | null>(
    todayInSalonTimezone(timezone),
  );
  // null = "use counted total" (the default UX). Letting the user type a
  // number switches into override mode, which the submit handler reads.
  const [nextStartingOverride, setNextStartingOverride] = useState<
    number | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Holds the closeout result so the modal can flip into a choice screen
  // (View Balancing Summary vs Go to New Cash Drawer) without unmounting.
  const [closeoutResult, setCloseoutResult] =
    useState<CountDrawerResponse | null>(null);

  // Reset on a real false→true transition only. SalonContext occasionally
  // refreshes businessInfo (and therefore `timezone`) mid-flow, so we
  // read `timezone` via a ref to keep it out of the effect deps —
  // re-running on a timezone change would wipe a freshly-set
  // `closeoutResult` and yank the user past the post-closeout choice
  // screen.
  const timezoneRef = useRef(timezone);
  useEffect(() => {
    timezoneRef.current = timezone;
  }, [timezone]);

  useEffect(() => {
    if (opened) {
      setDenominations(emptyDenominations());
      setCountedChecks(null);
      setBalancingDate(todayInSalonTimezone(timezoneRef.current));
      setNextStartingOverride(null);
      setError(null);
      setCloseoutResult(null);
    } else {
      // Modal just closed — drop any unconsumed closeout payload so the
      // next open lands cleanly on the count entry screen.
      setCloseoutResult(null);
    }
  }, [opened]);

  const countedTotal = denominationsToTotal(denominations);
  const expected = Number(session.expected_balance) || 0;
  // `check_sales` is the system-side total of check_sale rows logged this
  // session — what we *expect* the staff to have physically dropped into
  // the drawer in checks. Older sessions / pre-feature backends won't
  // surface it; fall back to 0 in that case.
  const expectedChecks = Number(session.metrics?.check_sales) || 0;
  const countedChecksValue = countedChecks ?? 0;
  const effectiveNextStarting =
    nextStartingOverride !== null ? nextStartingOverride : countedTotal;

  // Variance rolls cash and checks together — the drawer's job is to hold
  // both, so a $20 cash over with a $20 check short still nets to balanced.
  const variance = useMemo(
    () =>
      computeVariance(
        countedTotal + countedChecksValue,
        expected + expectedChecks,
      ),
    [countedTotal, countedChecksValue, expected, expectedChecks],
  );

  const handleSubmit = async () => {
    if (!balancingDate) {
      setError("Please select a Balancing Date");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await countCashDrawer(session.id, {
        counted_balance: countedTotal,
        counted_denominations: denominations.filter((d) => d.count > 0),
        counted_checks: countedChecks ?? undefined,
        balancing_date: balancingDate,
        next_drawer_starting_balance: effectiveNextStarting,
      });
      // Flip the modal into the choice screen; the parent doesn't get
      // notified yet so the old session stays visible underneath until
      // the user picks an action.
      setCloseoutResult(result);
    } catch (e: any) {
      setError(e?.message || "Failed to count drawer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToNewDrawer = () => {
    if (!closeoutResult) return;
    onCounted(closeoutResult);
    onClose();
  };

  const handleViewSummary = () => {
    if (!closeoutResult) return;
    onViewSummary(closeoutResult.closed_session);
    // The parent still needs the new_session swapped in behind the
    // summary so closing the summary lands on the freshly opened drawer.
    onCounted(closeoutResult);
    onClose();
  };

  const varianceColor =
    variance === 0 ? undefined : variance > 0 ? "green.7" : "red.7";
  const varianceLabel =
    variance < 0 ? "Short by" : variance > 0 ? "Over by" : "Balanced";

  // === Post-closeout choice screen ===
  if (closeoutResult) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Drawer Closed"
        size="lg"
        fullScreen={isMobile}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            What would you like to do next?
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <UnstyledButton onClick={handleViewSummary}>
              <Paper withBorder p="md" h="100%">
                <Stack gap={4} align="flex-start">
                  <IconReportAnalytics size={28} color="var(--brand-teal)" />
                  <Text fw={700}>View Balancing Summary</Text>
                  <Text size="sm" c="dimmed">
                    See the Counted Cash Summary and a full Daily Sales Summary
                    for the balancing date.
                  </Text>
                </Stack>
              </Paper>
            </UnstyledButton>
            <UnstyledButton onClick={handleGoToNewDrawer}>
              <Paper withBorder p="md" h="100%">
                <Stack gap={4} align="flex-start">
                  <IconCash size={28} color="var(--brand-teal)" />
                  <Text fw={700}>Go to New Cash Drawer</Text>
                  <Text size="sm" c="dimmed">
                    Land on the freshly opened drawer to keep ringing up
                    transactions.
                  </Text>
                </Stack>
              </Paper>
            </UnstyledButton>
          </SimpleGrid>
        </Stack>
      </Modal>
    );
  }

  // === Count entry screen ===
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Count Drawer"
      size="xl"
      fullScreen={isMobile}
      // Closing the modal by ESC / click-outside during submit would drop
      // the unrendered closeoutResult on the floor and the user would
      // never see the "Go to New Drawer vs View Summary" choice.
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        {/* Soaks up FocusTrap's initial autofocus so the DateInput below
            doesn't open its calendar / show a text-selection on mount. */}
        <Box
          data-autofocus
          tabIndex={-1}
          style={{ outline: "none" }}
          mb={-12}
        />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <DateInput
            label="Balancing Date"
            description="Used for calculating the Daily Sales Summary"
            value={balancingDate}
            onChange={(val) => setBalancingDate(val ?? null)}
            required
          />
          <NumberInput
            label="Checks"
            description="Total dollar amount of checks counted in the drawer"
            placeholder="0.00"
            value={countedChecks ?? ""}
            onChange={(val) => {
              if (val === "" || val === null || val === undefined) {
                setCountedChecks(null);
                return;
              }
              setCountedChecks(Number(val));
            }}
            min={0}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            leftSection="$"
            allowNegative={false}
          />
        </SimpleGrid>

        <DenominationInput
          value={denominations}
          onChange={setDenominations}
          showTotal={false}
        />

        <Paper
          withBorder
          p="sm"
          style={{ borderLeft: "4px solid var(--brand-teal)" }}
        >
          <Stack gap={6}>
            <Group justify="space-between" wrap="nowrap">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c="dimmed"
                style={{ letterSpacing: "0.08em" }}
              >
                Expected Cash
              </Text>
              <Text fz={22} fw={700} lh={1.1}>
                {formatCurrency(expected)}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c="dimmed"
                style={{ letterSpacing: "0.08em" }}
              >
                Expected Checks
              </Text>
              <Text fz={22} fw={700} lh={1.1}>
                {formatCurrency(expectedChecks)}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c="dimmed"
                style={{ letterSpacing: "0.08em" }}
              >
                Counted Cash
              </Text>
              <Text fz={22} fw={700} lh={1.1}>
                {formatCurrency(countedTotal)}
              </Text>
            </Group>
            <Group justify="space-between" wrap="nowrap">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c="dimmed"
                style={{ letterSpacing: "0.08em" }}
              >
                Counted Checks
              </Text>
              <Text fz={22} fw={700} lh={1.1}>
                {countedChecks === null
                  ? "—"
                  : formatCurrency(countedChecksValue)}
              </Text>
            </Group>
            <Divider variant="dashed" my={2} />
            <Group justify="space-between" wrap="nowrap">
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                c={varianceColor}
                style={{ letterSpacing: "0.08em" }}
              >
                {varianceLabel}
              </Text>
              <Text fz={22} fw={700} lh={1.1} c={varianceColor}>
                {variance === 0 ? "—" : formatCurrency(Math.abs(variance))}
              </Text>
            </Group>
          </Stack>
        </Paper>

        <NumberInput
          label="Next Drawer Starting Balance"
          description={
            nextStartingOverride === null
              ? "Defaults to the counted total"
              : "Override active"
          }
          value={effectiveNextStarting}
          onChange={(val) => {
            if (val === "" || val === null || val === undefined) {
              setNextStartingOverride(null);
              return;
            }
            setNextStartingOverride(Number(val));
          }}
          min={0}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          leftSection="$"
          allowNegative={false}
        />

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={submitting}
          >
            Count Drawer
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CountDrawerModal;
